/**
 * CR404: Category Branding Card Component
 * Matches Merchant Directory branding pattern with upload + URL + SVG + source + attribution
 */

import { useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Save, Loader2, FolderOpen, Upload, Trash2 } from "lucide-react";
import type { MerchantCategoryRow } from "@/hooks/useMerchantCategories";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CategoryBrandingCardProps {
  category: MerchantCategoryRow;
  onUpdate: (params: {
    categoryId: string;
    iconUrl?: string | null;
    iconSvg?: string | null;
    iconSource?: string | null;
    iconAttribution?: string | null;
  }) => Promise<void>;
  isUpdating?: boolean;
}

const ACCEPTED_FORMATS = ".png,.jpg,.jpeg,.svg";
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function CategoryBrandingCard({ category, onUpdate, isUpdating }: CategoryBrandingCardProps) {
  const [iconUrl, setIconUrl] = useState(category.icon_url ?? "");
  const [iconSvg, setIconSvg] = useState(category.icon_svg ?? "");
  const [iconSource, setIconSource] = useState(category.icon_source ?? "");
  const [iconAttribution, setIconAttribution] = useState(category.icon_attribution ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();

  // Sync state when category changes
  useState(() => {
    setIconUrl(category.icon_url ?? "");
    setIconSvg(category.icon_svg ?? "");
    setIconSource(category.icon_source ?? "");
    setIconAttribution(category.icon_attribution ?? "");
  });

  const hasChanges =
    iconUrl !== (category.icon_url ?? "") ||
    iconSvg !== (category.icon_svg ?? "") ||
    iconSource !== (category.icon_source ?? "") ||
    iconAttribution !== (category.icon_attribution ?? "");

  const handleSave = async () => {
    await onUpdate({
      categoryId: category.id,
      iconUrl: iconUrl || null,
      iconSvg: iconSvg || null,
      iconSource: iconSource || null,
      iconAttribution: iconAttribution || null,
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 5MB",
        variant: "destructive",
      });
      return;
    }

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PNG, JPG, or SVG file",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const timestamp = Date.now();
      const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const filePath = `categories/${category.slug}/${timestamp}-${sanitizedFilename}`;

      setUploadProgress(30);

      const { data, error: uploadError } = await supabase.storage
        .from("app-assets")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) throw uploadError;

      setUploadProgress(70);

      const { data: urlData } = supabase.storage
        .from("app-assets")
        .getPublicUrl(data.path);

      const publicUrl = urlData.publicUrl;

      setUploadProgress(90);

      await onUpdate({
        categoryId: category.id,
        iconUrl: publicUrl,
        iconSvg: null,
        iconSource: "uploaded",
        iconAttribution: iconAttribution || null,
      });

      setIconUrl(publicUrl);
      setIconSvg("");
      setIconSource("uploaded");
      setUploadProgress(100);

      toast({
        title: "Icon uploaded",
        description: "Category icon has been uploaded successfully",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveIcon = async () => {
    try {
      await onUpdate({
        categoryId: category.id,
        iconUrl: null,
        iconSvg: null,
        iconSource: null,
        iconAttribution: null,
      });

      setIconUrl("");
      setIconSvg("");
      setIconSource("");
      setIconAttribution("");

      toast({
        title: "Icon removed",
        description: "Category icon has been removed",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to remove icon";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const hasIcon = Boolean(iconUrl || iconSvg);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Branding</CardTitle>
        <CardDescription>Icon and attribution information.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Icon Preview */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden border">
            {iconSvg ? (
              <div
                className="w-12 h-12"
                dangerouslySetInnerHTML={{ __html: iconSvg }}
              />
            ) : iconUrl ? (
              <img
                src={iconUrl}
                alt={category.name}
                className="w-12 h-12 object-contain"
              />
            ) : (
              <FolderOpen className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">Current Icon</div>
            <div className="text-xs text-muted-foreground">
              {iconSvg ? "SVG" : iconUrl ? "URL" : "No icon set"}
            </div>
          </div>
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {/* Upload / Remove Actions */}
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS}
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading || isUpdating}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isUpdating}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Icon
          </Button>
          {hasIcon && (
            <Button
              variant="outline"
              onClick={handleRemoveIcon}
              disabled={isUploading || isUpdating}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove Icon
            </Button>
          )}
        </div>

        <div className="space-y-2">
          <Label>Icon URL</Label>
          <Input
            value={iconUrl}
            onChange={(e) => setIconUrl(e.target.value)}
            placeholder="https://..."
            disabled={isUploading}
          />
        </div>

        <div className="space-y-2">
          <Label>Icon SVG</Label>
          <Textarea
            value={iconSvg}
            onChange={(e) => setIconSvg(e.target.value)}
            placeholder="<svg>...</svg>"
            className="font-mono text-xs h-24"
            disabled={isUploading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Source</Label>
            <Input
              value={iconSource}
              onChange={(e) => setIconSource(e.target.value)}
              placeholder="e.g., brand site"
              disabled={isUploading}
            />
          </div>
          <div className="space-y-2">
            <Label>Attribution</Label>
            <Input
              value={iconAttribution}
              onChange={(e) => setIconAttribution(e.target.value)}
              placeholder="e.g., © Brand Name"
              disabled={isUploading}
            />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={!hasChanges || isUpdating || isUploading}
          className="w-full"
        >
          {isUpdating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Branding
        </Button>
      </CardContent>
    </Card>
  );
}
